"use client";

import { useEffect, useRef } from "react";

type Star = {
  x: number;
  y: number;
  r: number;
  base: number; // base alpha
  amp: number; // twinkle amplitude
  speed: number; // radians / second
  phase: number;
  vx: number; // drift, px / second
  vy: number;
  purple: boolean;
};

/**
 * Star canvas that fills its nearest positioned ancestor.
 *
 * By default it draws once (static — used by the menu overlay). Pass
 * `twinkle` to gently breathe the stars' brightness and slowly drift them
 * across the panel via requestAnimationFrame (brighter stars drift a touch
 * faster, giving a subtle parallax). The loop is paused while the canvas is
 * off-screen and disabled entirely for users who prefer reduced motion, so it
 * stays cheap and unobtrusive.
 */
export default function Starfield({
  className,
  twinkle = false,
  maxStars = 200,
}: {
  className?: string;
  twinkle?: boolean;
  maxStars?: number;
}) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    const parent = canvas?.parentElement;
    if (!canvas || !parent) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let stars: Star[] = [];
    let w = 0;
    let h = 0;
    let dpr = 1;

    const build = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = parent.clientWidth;
      h = parent.clientHeight;
      if (!w || !h) return;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const count = Math.min(maxStars, Math.round((w * h) / 6500));
      stars = Array.from({ length: count }, () => {
        const base = Math.random() * 0.5 + 0.15;
        const r = Math.random() * 1.4 + 0.4;
        // gentle leftward drift, scaled by size for a parallax feel
        const drift = r * 2.4 + 0.4; // px / second
        const ang = Math.PI * 0.92 + (Math.random() - 0.5) * 0.7;
        return {
          x: Math.random() * w,
          y: Math.random() * h,
          r,
          base,
          // twinkle only a portion of stars, gently
          amp: Math.random() < 0.7 ? base * (Math.random() * 0.45 + 0.2) : 0,
          speed: Math.random() * 0.9 + 0.3,
          phase: Math.random() * Math.PI * 2,
          vx: Math.cos(ang) * drift,
          vy: Math.sin(ang) * drift,
          purple: Math.random() < 0.32,
        };
      });
    };

    const render = (t: number, dt: number) => {
      if (!w || !h) return;
      ctx.clearRect(0, 0, w, h);
      for (const s of stars) {
        if (dt > 0) {
          s.x += s.vx * dt;
          s.y += s.vy * dt;
          // wrap around the edges so the field never empties out
          if (s.x < -2) s.x = w + 2;
          else if (s.x > w + 2) s.x = -2;
          if (s.y < -2) s.y = h + 2;
          else if (s.y > h + 2) s.y = -2;
        }
        const alpha =
          s.amp > 0
            ? Math.max(0.04, s.base + Math.sin(t * s.speed + s.phase) * s.amp)
            : s.base;
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fillStyle = s.purple
          ? `rgba(192,132,252,${alpha})`
          : `rgba(255,255,255,${alpha})`;
        ctx.fill();
      }
    };

    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const animate = twinkle && !reduced;

    let raf = 0;
    let running = false;
    let start = 0;
    let last = 0;

    const loop = (now: number) => {
      if (!start) start = now;
      if (!last) last = now;
      // clamp dt so drifting doesn't jump after a pause / tab switch
      const dt = Math.min((now - last) / 1000, 0.05);
      last = now;
      render((now - start) / 1000, dt);
      raf = requestAnimationFrame(loop);
    };
    const play = () => {
      if (running) return;
      running = true;
      last = 0;
      raf = requestAnimationFrame(loop);
    };
    const pause = () => {
      running = false;
      cancelAnimationFrame(raf);
    };

    const rebuild = () => {
      build();
      if (!animate) render(0, 0);
    };
    rebuild();
    window.addEventListener("resize", rebuild);

    // Only animate while visible (and the tab is active).
    let io: IntersectionObserver | undefined;
    const onVisibility = () => (document.hidden ? pause() : undefined);
    if (animate) {
      play();
      document.addEventListener("visibilitychange", onVisibility);
      io = new IntersectionObserver(
        (entries) => (entries[0].isIntersecting ? play() : pause()),
        { threshold: 0 }
      );
      io.observe(canvas);
    }

    return () => {
      window.removeEventListener("resize", rebuild);
      document.removeEventListener("visibilitychange", onVisibility);
      io?.disconnect();
      pause();
    };
  }, [twinkle, maxStars]);

  return (
    <canvas
      ref={ref}
      className={`absolute inset-0 h-full w-full ${className ?? ""}`}
      aria-hidden
    />
  );
}
